import React, { useState, useEffect } from 'react';
import { getEducation } from '../services/api';
import { GraduationCap, ExternalLink } from 'lucide-react';

const EducationSection = () => {
  const [education, setEducation] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getEducation();
        setEducation(data || []);
      } catch (error) {
        console.error('Error fetching education:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <div className="w-16 h-16 border-4 border-slate-700 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-10 right-10 w-64 h-64 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full blur-3xl opacity-20" />
      
      <div className="container mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center shadow-lg shadow-slate-500/30">
              <GraduationCap size={28} className="text-white" />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 text-slate-800">
            Education
          </h2>
          <p className="text-lg text-slate-600 font-light leading-relaxed">
            Building foundations for lifelong learning
          </p>
        </div>

        {/* Education Cards - Horizontal Layout */}
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {education.map((edu, index) => (
              <a
                key={index}
                href={edu.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-slate-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl text-center"
              >
                {/* School Logo */}
                <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center">
                  {edu.logo ? (
                    <img 
                      src={edu.logo} 
                      alt={edu.school}
                      className="max-w-full max-h-full object-contain filter group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center ${edu.logo ? 'hidden' : ''}`}
                  >
                    <GraduationCap size={40} className="text-slate-400" />
                  </div>
                </div>
                
                {/* Degree */}
                <h3 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-slate-900 transition-colors">
                  {edu.degree}
                </h3>
                
                {/* School Name */}
                <p className="text-slate-600 font-medium mb-1">
                  {edu.school}
                </p>
                
                {/* Years */}
                {edu.years && (
                  <p className="text-slate-400 text-sm mb-2">
                    {edu.years}
                  </p>
                )}
                
                {/* Program/Description */}
                <p className="text-slate-500 text-sm mb-4">
                  {edu.program}
                </p>
                
                {/* Link indicator */}
                <div className="flex items-center justify-center gap-2 text-slate-400 group-hover:text-slate-600 transition-colors">
                  <ExternalLink size={14} />
                  <span className="text-xs font-medium">View Program</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationSection;
